import React, { useEffect, useState } from 'react';
import { Modal, Carousel } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DeleteModal from './DeleteModal';
import Loader from './Loader';
import { deleteFlaggedActivity } from '../controller';
import { requestHandler } from '../helper';
import { formatDate } from '../helper/common';
import { getImageUrl, getThumbnailUrl, profilePhotoUrl } from '../helper/image';

const isVideo = (media) => !!(media?.type && media.type.startsWith('video/'));
const accountName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.userName || '-';

const playBadge = (
    <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: 'rgba(0,0,0,0.7)', borderRadius: '50%', width: '40px', height: '40px',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
    </div>
);

const MediaTile = ({ media, index, onClick }) => (
    <div
        onClick={() => onClick(media)}
        style={{
            width: '150px', height: '150px', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
            border: '2px solid #ddd', position: 'relative', transition: 'transform 0.2s, box-shadow 0.2s', margin: '0 auto'
        }}
        onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'; }}
        onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
        {isVideo(media) ? (
            <video style={{ width: '100%', height: '100%', objectFit: 'cover' }} src={getImageUrl(media.url)} />
        ) : (
            <img src={getThumbnailUrl(media)} alt={`Media ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {isVideo(media) && playBadge}
    </div>
);

const MediaView = ({ media, alt }) => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
        {isVideo(media) ? (
            <video
                controls
                style={{ maxHeight: '450px', maxWidth: '100%', borderRadius: '8px' }}
                onError={(e) => { e.target.style.display = 'none'; }}
            >
                <source src={getImageUrl(media.url)} type={media.type} />
                Your browser does not support the video tag.
            </video>
        ) : (
            <img src={getThumbnailUrl(media)} alt={alt} style={{ maxHeight: '450px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }} />
        )}
    </div>
);

/**
 * Gallery of activity posts (carousel, "View All" grid, preview) with an account block and a
 * "Delete" that removes the whole activity post.
 *   activities: [{ _id, activity_image: [...], des?, createdAt?, isPrivate? }]
 *   user:       account the activities belong to ({ firstName, lastName, userName, email, imageURL })
 *   onDeleted:  called after an activity was deleted, so the page can refresh its list
 */
function UserActivitiesModal({ show, onClose, activities = [], user, onDeleted, startMediaId }) {
    const [items, setItems] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [mediaIndex, setMediaIndex] = useState(0);
    const [viewAll, setViewAll] = useState(false);
    const [preview, setPreview] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const [loading, setLoading] = useState(false);

    // start from the given activities each time the modal opens
    useEffect(() => {
        if (show) {
            setItems(activities);
            setActiveIndex(0);
            // open on the photo that was clicked (startMediaId), otherwise the first one
            const start = (activities[0]?.activity_image || []).findIndex((m) => m._id === startMediaId);
            setMediaIndex(Math.max(start, 0));
            setViewAll(false);
            setPreview(null);
            setToDelete(null);
        }
        // eslint-disable-next-line
    }, [show]);

    const deleteActivity = async () => {
        if (!toDelete?._id) return;
        await requestHandler(
            async () => await deleteFlaggedActivity(toDelete._id),
            setLoading,
            (res) => {
                toast.success(res.message || 'Activity deleted successfully');
                const remaining = items.filter((a) => a._id !== toDelete._id);
                setItems(remaining);
                setActiveIndex((i) => Math.min(i, Math.max(remaining.length - 1, 0)));
                setToDelete(null);
                setViewAll(false);
                if (onDeleted) onDeleted(toDelete._id);
                if (remaining.length === 0) onClose();
            },
            () => { }
        );
    };

    const current = items[activeIndex];
    const photo = profilePhotoUrl(user);
    const showMain = show && !preview && !toDelete;

    const title = (
        <h5 style={{ marginBottom: '8px', color: '#333', fontWeight: '600' }}>
            Activity {activeIndex + 1} of {items.length}
        </h5>
    );

    const meta = (activity) => {
        const parts = [
            activity?.createdAt ? formatDate(activity.createdAt) : null,
            activity?.des || null,
            activity?.isPrivate === undefined ? null : (activity.isPrivate ? 'Private' : 'Public'),
        ].filter(Boolean);
        return parts.length ? <p className="mb-0" style={{ color: '#6c757d', fontSize: '13px' }}>{parts.join(' · ')}</p> : null;
    };

    return (
        <>
            {loading && <Loader />}

            <DeleteModal
                show={!!toDelete}
                handleClose={() => setToDelete(null)}
                handleDelete={deleteActivity}
                title="Delete Activity"
                message="Are you sure you want to delete this activity? This action cannot be undone."
            />

            <Modal show={showMain} onHide={onClose} centered size="lg">
                <style>
                    {`
                        .carousel-control-prev-icon,
                        .carousel-control-next-icon { filter: invert(1); }
                        .carousel-control-prev,
                        .carousel-control-next { opacity: 1; }
                        .carousel-control-prev:hover,
                        .carousel-control-next:hover { opacity: 0.8; }
                        .outer-carousel-single > .carousel-control-prev,
                        .outer-carousel-single > .carousel-control-next { display: none !important; }
                    `}
                </style>
                <Modal.Header closeButton>
                    <Modal.Title>User Activities</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {user && (
                        <div className="d-flex align-items-center gap-3 mb-3">
                            {photo ? (
                                <img src={photo} alt="user" width={48} height={48} className="rounded-circle object-fit-cover" />
                            ) : null}
                            <div>
                                <div className="fw-600">{accountName(user)}</div>
                                {user.userName && <div className="text-light">@{user.userName}</div>}
                                {user.email && <div className="text-light">{user.email}</div>}
                            </div>
                        </div>
                    )}

                    {viewAll && current ? (
                        <div style={{ minHeight: '400px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '20px', position: 'relative' }}>
                            <div style={{ marginBottom: '20px', textAlign: 'center', position: 'relative' }}>
                                <button
                                    onClick={() => setViewAll(false)}
                                    style={{
                                        position: 'absolute', top: '0', left: '0', background: '#6c757d', color: '#fff', border: 'none',
                                        borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontSize: '14px'
                                    }}
                                >
                                    ← Back
                                </button>
                                {title}
                                {meta(current)}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', padding: '10px' }}>
                                {current.activity_image?.map((media, i) => (
                                    <MediaTile key={i} media={media} index={i} onClick={setPreview} />
                                ))}
                            </div>
                        </div>
                    ) : items.length > 0 && (
                        <Carousel
                            interval={null}
                            className={items.length === 1 ? 'outer-carousel-single' : ''}
                            controls={items.length > 1}
                            indicators={false}
                            activeIndex={activeIndex}
                            onSelect={(i) => { setActiveIndex(i); setMediaIndex(0); }}
                        >
                            {items.map((activity, index) => {
                                const media = activity?.activity_image || [];
                                return (
                                    <Carousel.Item key={activity._id || index}>
                                        <div style={{ minHeight: '400px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '20px' }}>
                                            <div style={{ marginBottom: '20px', textAlign: 'center', position: 'relative' }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setToDelete(activity); }}
                                                    style={{
                                                        position: 'absolute', top: '0', right: '0', background: '#dc3545', color: '#fff', border: 'none',
                                                        borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px',
                                                        display: 'flex', alignItems: 'center', gap: '4px', zIndex: 10
                                                    }}
                                                    title="Delete Activity"
                                                >
                                                    <img src="/images/svg/delete.svg" alt="delete" width={14} height={14} />
                                                    Delete
                                                </button>
                                                {title}
                                                {meta(activity)}
                                            </div>

                                            {media.length > 1 && (
                                                <div style={{ position: 'absolute', bottom: '20px', right: '20px', zIndex: 10 }}>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setViewAll(true); }}
                                                        style={{
                                                            background: '#007bff', color: '#fff', border: 'none', borderRadius: '20px', padding: '8px 16px',
                                                            cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center',
                                                            gap: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                                        }}
                                                    >
                                                        <span>View All</span>
                                                        <span>({media.length})</span>
                                                    </button>
                                                </div>
                                            )}

                                            {media.length > 1 ? (
                                                <Carousel interval={null} indicators={false} controls={false} activeIndex={mediaIndex} onSelect={setMediaIndex}>
                                                    {media.map((m, i) => (
                                                        <Carousel.Item key={i}>
                                                            <MediaView media={m} alt={`Media ${i + 1}`} />
                                                        </Carousel.Item>
                                                    ))}
                                                </Carousel>
                                            ) : media.length === 1 ? (
                                                <MediaView media={media[0]} alt="Activity Media" />
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px', color: '#999' }}>
                                                    No media available
                                                </div>
                                            )}
                                        </div>
                                    </Carousel.Item>
                                );
                            })}
                        </Carousel>
                    )}
                </Modal.Body>
            </Modal>

            {/* Image Preview Modal */}
            {preview && (
                <Modal show onHide={() => setPreview(null)} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Preview</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', backgroundColor: '#f8f9fa' }}>
                            {isVideo(preview) ? (
                                <video controls autoPlay style={{ maxHeight: '500px', maxWidth: '100%', borderRadius: '8px' }} src={getImageUrl(preview.url)}>
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <img src={getThumbnailUrl(preview)} alt="Preview" style={{ maxHeight: '500px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }} />
                            )}
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
}

export default UserActivitiesModal;
